import React from 'react';
import { Link } from 'react-router-dom';
import { Avatar, Tooltip, Chip } from '@heroui/react';
import { useUser } from '../../../contexts/UserContext.jsx';
import { Crown, Star } from 'lucide-react';
import Balatro from '../../ui/Balatro.jsx';
import { cn } from '../../../utils/cn.js';

const GiftBoxArt = ({ compact = false, small = false }) => (
    <div className={cn(
        "invite-gift-scene relative mx-auto",
        compact ? "h-10 w-10" : small ? "mb-2 h-[70px] w-20" : "mb-3 h-[104px] w-[120px]"
    )}>
        {!compact && (
            <>
                <span className="invite-spark absolute left-3 top-3 h-1.5 w-1.5 rounded-full bg-violet-300" />
                <span className="invite-spark absolute right-5 top-1 h-1.5 w-1.5 rounded-full bg-sky-300 [animation-delay:0.35s]" />
                <span className="invite-spark absolute bottom-9 left-1 h-1 w-1 rounded-full bg-violet-300 [animation-delay:0.75s]" />
                <span className="invite-spark absolute bottom-7 right-2 h-1.5 w-1.5 rounded-full bg-amber-300 [animation-delay:1s]" />
                <span className="invite-spark absolute right-1 top-8 h-1 w-1 rounded-full bg-sky-300 [animation-delay:0.2s]" />
            </>
        )}

        <div className={cn(
            "absolute left-1/2 z-30 -translate-x-1/2",
            compact ? "bottom-[27px] h-4 w-7" : small ? "bottom-[45px] h-4 w-7" : "bottom-[66px] h-6 w-10"
        )}>
            <div className={cn(
                "absolute bottom-0 left-0 origin-bottom-right rotate-[-35deg] rounded-[50%_0_50%_50%] bg-gradient-to-br from-red-500 to-rose-400",
                compact || small ? "h-4 w-4" : "h-5 w-5"
            )} />
            <div className={cn(
                "absolute bottom-0 right-0 origin-bottom-left rotate-[35deg] scale-x-[-1] rounded-[50%_0_50%_50%] bg-gradient-to-br from-red-500 to-rose-400",
                compact || small ? "h-4 w-4" : "h-5 w-5"
            )} />
            <div className={cn(
                "absolute left-1/2 -translate-x-1/2 rounded-full bg-red-500",
                compact || small ? "bottom-0.5 h-2.5 w-2.5" : "bottom-0.5 h-3 w-3"
            )} />
        </div>

        <div className={cn(
            "absolute left-1/2 -translate-x-1/2 rounded-t-md bg-gradient-to-br from-violet-700 to-violet-500",
            compact ? "bottom-[21px] h-2.5 w-9" : small ? "bottom-8 h-3.5 w-[52px]" : "bottom-12 h-5 w-[76px]"
        )} />
        <div className={cn(
            "absolute left-1/2 z-20 -translate-x-1/2 bg-gradient-to-r from-red-500 via-rose-400 to-red-500",
            compact ? "bottom-3 h-1.5 w-9" : small ? "bottom-[19px] h-2 w-[52px]" : "bottom-7 h-2.5 w-[76px]"
        )} />
        <div className={cn(
            "absolute bottom-0 left-1/2 -translate-x-1/2 rounded-md bg-gradient-to-br from-violet-600 via-violet-500 to-violet-300",
            compact ? "h-7 w-8" : small ? "h-9 w-[46px]" : "h-14 w-[68px]"
        )}>
            <div className={cn(
                "absolute inset-y-0 left-1/2 z-20 -translate-x-1/2 bg-gradient-to-b from-red-500 via-rose-400 to-red-500",
                compact ? "w-1.5" : small ? "w-2" : "w-3"
            )} />
            <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 bg-white/15" style={{ width: compact ? 6 : small ? 8 : 12 }} />
        </div>
    </div>
)

const InvitePromoCard = ({ collapsed = false }) => {
    if (collapsed) {
        return (
            <Tooltip>
                <Link
                    to="/invite"
                    aria-label="邀请好友活动"
                    className="relative flex h-12 w-12 items-center justify-center rounded-2xl border border-violet-200 bg-gradient-to-br from-violet-50 to-sky-50 shadow-md shadow-violet-500/10 transition-colors hover:border-violet-300 dark:border-violet-500/20 dark:from-violet-950 dark:to-sky-950"
                >
                    <GiftBoxArt compact />
                </Link>
                <Tooltip.Content placement="right">
                    <div className="px-1 py-2">
                        <div className="font-bold text-white">邀请好友</div>
                        <div className="text-xs text-slate-400">双方各得 20% 返现</div>
                    </div>
                </Tooltip.Content>
            </Tooltip>
        )
    }

    return (
        <Link
            to="/invite"
            className="group relative block w-full overflow-hidden rounded-2xl border border-violet-200/80 bg-gradient-to-br from-violet-50 to-sky-50 p-3 shadow-lg shadow-violet-500/10 transition-colors duration-200 hover:border-violet-300 hover:bg-violet-50 dark:border-violet-500/20 dark:from-violet-950/80 dark:to-sky-950/70"
        >
            <div className="pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full bg-violet-300/30 blur-2xl dark:bg-violet-500/20" />
            <div className="relative text-center">
                <p className="m-0 text-sm font-bold tracking-wide text-violet-950 dark:text-violet-50">邀请好友</p>
                <p className="mb-1.5 mt-0.5 text-[11px] font-semibold text-sky-600 dark:text-sky-300">双方各得 20% 返现</p>
                <GiftBoxArt small />
                <div className="flex h-8 w-full items-center justify-center rounded-full bg-white text-xs font-bold tracking-wide text-violet-700 shadow-md shadow-violet-500/10 transition-colors group-hover:bg-violet-50 dark:bg-white/95 dark:group-hover:bg-violet-100">
                    立即邀请
                </div>
            </div>
        </Link>
    )
}

const SidebarFooter = ({ collapsed }) => {
    const { user } = useUser();

    const userAvatar = user?.avatar_url || `https://i.pravatar.cc/150?u=${user?.email || 'admin@v2board.com'}`;
    const userName = user?.nickname || 'Dragon Admin';
    const userDesc = user?.email || 'Premium Access';

    if (collapsed) {
        return (
            <div className="p-4 flex flex-col items-center gap-4">
                <InvitePromoCard collapsed />
                <Tooltip>
                    <div className="relative group">
                        <Avatar
                            color="warning"
                            size="sm"
                        >
                            <Avatar.Image src={userAvatar} alt={userName} />
                            <Avatar.Fallback>
                                {userName?.[0]?.toUpperCase() ?? 'U'}
                            </Avatar.Fallback>
                        </Avatar>
                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-gradient-to-tr from-amber-400 to-orange-500 rounded-full border border-slate-950 flex items-center justify-center">
                            <Star className="w-2 h-2 text-white" fill="white" />
                        </div>
                    </div>
                    <Tooltip.Content placement="right">
                        <div className="px-1 py-2 text-center">
                            <div className="font-bold text-white">{userName}</div>
                            <div className="text-xs text-slate-400">{userDesc}</div>
                        </div>
                    </Tooltip.Content>
                </Tooltip>
            </div>
        );
    }

    return (
        <div className="p-4 mt-auto relative space-y-3">
            <InvitePromoCard />

            <div className="relative group">
            <div className="relative w-full rounded-2xl overflow-hidden border border-white/10 shadow-lg transition-transform duration-300 group-hover:scale-[1.02]">
                {/* Balatro Background Effect - Fixed size to prevent resize flickering */}
                <div className="absolute inset-0 z-0 opacity-75 overflow-hidden">
                    <div className="absolute inset-0" style={{ minWidth: '280px', width: '280px' }}>
                        <Balatro
                            isRotate
                            mouseInteraction={false}
                            pixelFilter={700}
                            color1="#1e293b"
                            color2="#4f46e5"
                            color3="#d946ef"
                        />
                    </div>
                </div>

                {/* Glass Overlay for Readability */}
                <div className="absolute inset-0 z-10 bg-slate-900/40 backdrop-blur-[1px]" />

                {/* Content */}
                <div className="relative z-20 p-3 flex items-center gap-3">
                    <Avatar
                        color="accent"
                        size="md"
                        className="flex-shrink-0"
                    >
                        <Avatar.Image src={userAvatar} alt={userName} />
                        <Avatar.Fallback>
                            {userName?.[0]?.toUpperCase() ?? 'U'}
                        </Avatar.Fallback>
                    </Avatar>
                    <div className="flex-1 min-w-0 overflow-hidden">
                        <div className="flex items-center gap-2">
                            <span className="font-bold text-sm truncate text-white">
                                {userName}
                            </span>
                            <Chip
                                size="sm"
                                variant="soft"
                                color="warning"
                            >
                                <Crown size={8} strokeWidth={3} />
                                <span>VIP</span>
                            </Chip>
                        </div>
                        <span className="text-xs text-slate-300 truncate block">
                            {userDesc}
                        </span>
                    </div>
                </div>
            </div>
            </div>
        </div>
    );
};

export default SidebarFooter;
